import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Camera, MapPin, Upload } from "lucide-react";
import MemberGate from "../components/MemberGate";
import PhotoStage from "../components/PhotoStage";
import { ErrorMessage, Loading } from "../components/Loading";
import { api } from "../lib/api";
import { useApi } from "../hooks/useApi";

export default function ReunionDetail({ authenticated, canContribute, isAdministrator, onLogin, version, onUpdated }) {
  const { reunionId } = useParams();
  const [selectedAlbum, setSelectedAlbum] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [movingPhotoId, setMovingPhotoId] = useState("");
  const [deletingPhotoId, setDeletingPhotoId] = useState("");
  const [moveError, setMoveError] = useState("");
  const [moveNotice, setMoveNotice] = useState("");
  const loader = useCallback(
    () => authenticated ? api(`/api/reunions?v=${version}`) : Promise.resolve(null),
    [authenticated, version],
  );
  const { data, error, loading, reload, setData } = useApi(loader, [loader]);
  const reunion = data?.reunions?.find((item) => item.id === reunionId);
  const albums = useMemo(() => {
    if (!reunion) return [];
    const legacy = (reunion.albums || []).map((id) => data?.albums?.find((album) => album.id === id)).filter(Boolean);
    const uploaded = reunion.photos?.length ? [{ id: `${reunion.id}-shared`, title: "Shared by classmates", description: "Photographs added by the class.", photos: reunion.photos }] : [];
    return [...legacy, ...uploaded];
  }, [data, reunion]);

  async function uploadPhotos(event) {
    const files = [...(event.target.files || [])];
    if (!files.length) return;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (files.length > 10 || totalBytes > 25 * 1024 * 1024 || files.some((file) => file.size > 15 * 1024 * 1024)) {
      setUploadError("Choose up to 10 photographs totaling no more than 25 MB, with each image under 15 MB.");
      event.target.value = "";
      return;
    }
    setUploading(true);
    setUploadError("");
    const body = new FormData();
    files.forEach((file) => body.append("photos", file));
    try {
      await api(`/api/reunions/${reunion.id}/photos`, { method: "POST", body });
      onUpdated();
      await reload();
    } catch (requestError) {
      setUploadError(requestError.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function moveToCollegeDays(photo) {
    if (!activeAlbum || movingPhotoId || deletingPhotoId) return;
    const albumId = activeAlbum.id;
    setMovingPhotoId(photo.id);
    setMoveError("");
    setMoveNotice("");
    removePhotoFromView(albumId, photo.id);
    try {
      await api("/api/admin/college-days", {
        method: "POST",
        body: JSON.stringify({ albumId, photoId: photo.id, description: "" }),
      });
      setMoveNotice(`${photo.alt || "Photograph"} moved to College Days.`);
    } catch (requestError) {
      setMoveError(requestError.message);
      await reload();
    } finally {
      setMovingPhotoId("");
    }
  }

  function removePhotoFromView(albumId, photoId) {
    setData((current) => current ? {
      ...current,
      albums: (current.albums || []).map((album) => album.id === albumId
        ? { ...album, photos: album.photos.filter((item) => item.id !== photoId) }
        : album),
      reunions: (current.reunions || []).map((item) => `${item.id}-shared` === albumId
        ? { ...item, photos: (item.photos || []).filter((candidate) => candidate.id !== photoId) }
        : item),
    } : current);
  }

  async function deletePhoto(photo) {
    if (!activeAlbum || movingPhotoId || deletingPhotoId) return;
    const albumId = activeAlbum.id;
    setDeletingPhotoId(photo.id);
    setMoveError("");
    setMoveNotice("");
    removePhotoFromView(albumId, photo.id);
    try {
      await api("/api/admin/reunion-photos", {
        method: "DELETE",
        body: JSON.stringify({ albumId, photoId: photo.id }),
      });
      setMoveNotice(`${photo.alt || "Photograph"} deleted.`);
    } catch (requestError) {
      setMoveError(requestError.message);
      await reload();
    } finally {
      setDeletingPhotoId("");
    }
  }

  if (!authenticated) {
    return <div className="reunion-detail-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }
  if (loading) return <div className="section-wrap"><Loading /></div>;
  if (error) return <div className="section-wrap"><ErrorMessage>{error}</ErrorMessage></div>;
  if (!reunion) return <div className="section-wrap"><ErrorMessage>That reunion could not be found.</ErrorMessage></div>;

  const activeAlbum = albums[selectedAlbum];
  return (
    <>
      <section className="reunion-detail-hero">
        {reunion.cover && <img src={reunion.cover} alt="" />}
        <div className="reunion-detail-hero__shade" />
        <div className="reunion-detail-hero__content section-wrap">
          <Link className="back-link" to="/reunions"><ArrowLeft aria-hidden="true" /> All reunions</Link>
          <p className="kicker">{reunion.eyebrow} · {reunion.year}</p>
          <h1>{reunion.title}</h1>
          <div className="reunion-detail-hero__facts">
            <span><CalendarDays aria-hidden="true" /> {reunion.date}{reunion.endDate ? ` — ${reunion.endDate}` : ""}</span>
            <span><MapPin aria-hidden="true" /> {reunion.location}</span>
          </div>
        </div>
      </section>

      <section className="reunion-story section-wrap">
        <div>
          <p className="kicker">The chapter</p>
          <h2>{reunion.description}</h2>
        </div>
        <aside>
          {reunion.organizer && <p><b>Organized by</b><br />{reunion.organizer}</p>}
          {reunion.contact && <p><b>Contact</b><br />{reunion.contact}</p>}
          {reunion.schedule?.length > 0 && <ul>{reunion.schedule.map((item) => <li key={item}>{item}</li>)}</ul>}
        </aside>
      </section>

      <section className="album-section section-wrap">
        <div className="album-section__heading">
          <div><p className="kicker">The photographs</p><h2>One frame at a time.</h2></div>
          {albums.length > 0 && (canContribute ? (
            <label className="button button--coral upload-button">
              <Upload aria-hidden="true" /> {uploading ? "Uploading…" : "Add photographs"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple disabled={uploading} onChange={uploadPhotos} />
            </label>
          ) : !authenticated ? (
            <button className="button button--ghost" type="button" onClick={() => setShowGate(true)}><Camera aria-hidden="true" /> Sign in to add photos</button>
          ) : null)}
        </div>
        {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
        {moveError && <ErrorMessage>{moveError}</ErrorMessage>}
        <p className="sr-only" role="status" aria-live="polite">{moveNotice}</p>
        {albums.length ? (
          <>
            <div className="album-tabs" role="tablist" aria-label="Reunion albums">
              {albums.map((album, index) => (
                <button key={album.id} className={index === selectedAlbum ? "is-active" : ""} type="button" role="tab" aria-selected={index === selectedAlbum} onClick={() => setSelectedAlbum(index)}>
                  <span>{String(index + 1).padStart(2, "0")}</span>{album.title}<small>{album.locked ? "Members only" : `${album.photos.length} frames`}</small>
                </button>
              ))}
            </div>
            <div className="album-intro"><h3>{activeAlbum?.title}</h3><p>{activeAlbum?.description}</p></div>
            <PhotoStage
              album={activeAlbum}
              onUnlock={() => setShowGate(true)}
              curation={isAdministrator ? {
                movingPhotoId,
                move: moveToCollegeDays,
                deletingPhotoId,
                delete: deletePhoto,
                busy: Boolean(movingPhotoId || deletingPhotoId),
              } : null}
            />
          </>
        ) : (
          <PhotoStage
            album={{ photos: [] }}
            emptyAction={canContribute ? (
              <label className="button button--ink upload-button">
                <Upload aria-hidden="true" /> {uploading ? "Uploading…" : "Add the first photographs"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple disabled={uploading} onChange={uploadPhotos} />
              </label>
            ) : !authenticated ? (
              <button className="button button--ink" type="button" onClick={() => setShowGate(true)}><Camera aria-hidden="true" /> Sign in to add photographs</button>
            ) : null}
          />
        )}
      </section>

      {showGate && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setShowGate(false)}>
          <div className="modal-panel modal-panel--gate" role="dialog" aria-modal="true"><MemberGate compact onSuccess={async () => { await onLogin(); await reload(); setShowGate(false); }} /></div>
        </div>
      )}
    </>
  );
}
